import { applyMiddleware, compose, legacy_createStore as createStore } from 'redux';
import {
    getConfigListFilters,
    getConfigListSort,
    getConfigListSortDirection,
} from './consent/state/ConfigList.selectors';
import {
    getPersistedActionItemListState,
    getPersistedFormListState,
    getPersistedFormListViewState,
    getPersistedRequestListState,
    getPersistedWorkflowListState,
} from './dsar/state/DSARPersist.selectors';
import {
    getPersistedConsumerSsoListState,
    getPersistedDashboardState,
    getPersistedIntegrationsListState,
    getPersistedPrivacyProtocolListState,
    getPersistedUCConfigListState,
} from './uc/state/Dashboard.selectors';

import AmplifyMiddleware from './authentication/state/amplify/Amplify.middleware';
import { BROWSE_CUSTOMER } from './common/services/customer';
import { CLASSIFICATION_VIEWS } from './consent/routes/ConfigDetails/components/Classification/Classification.constants';
import DBActions from './common/state/db/DB.actions';
import { types as NavActionTypes } from './common/state/nav/Nav.actions';
import { STORE_TYPES } from './consent/constants';
import customerSchema from './common/state/db/customer/Customer.schema';
import { formatCustomer } from './common/services/format';
import fromState from './common/state/selectors';
import { getPersistedClassification } from './consent/state/tattles/Tattles.selectors';
import { getPersistedVendorListState } from './vendors/state/VendorPersist.selectors';
import merge from 'lodash/merge';
import middleware from './common/middleware';
import reduceReducers from 'reduce-reducers';
import reduxThunk from 'redux-thunk';
import rootReducer from './common/state/reducer';
import { setStoreRef } from './storeRef';

const isDev = ['dev', 'development', 'ci'].includes(process.env.MODE);

const ignoredActions = [
    'Amplify',
    'auth.user.notifications/REFRESH_BEGIN',
    'auth.user.notifications/REFRESH_SUCCESS',
    'auth.user.notifications/REFRESH_FAILURE',
];
if (!isDev) {
    ignoredActions.push('ui/');
    ignoredActions.push('DB/UPDATE');
    ignoredActions.push('@@');
}

const actionSanitizer = action => {
    const { meta: providedMeta } = action;
    const { schema: providedSchema, ...remainingMeta } = providedMeta || {};
    const meta = {
        ...remainingMeta,
        schema: providedSchema
            ? {
                  key: providedSchema.key ?? 'Non-Entity Schema',
                  idAttribute: providedSchema.idAttribute ?? '',
              }
            : undefined,
    };
    return { ...action, meta };
};

export const localStorageKey = 'state';
// Get any initial query parameters and store them
const params = new URLSearchParams(window.location.search);
const urlParams = {};
for (const [key, value] of params.entries()) {
    urlParams[key] = value;
}

const getStorageState = () => {
    try {
        const serializedState = localStorage.getItem(localStorageKey);
        if (serializedState === null) {
            return {};
        }
        return JSON.parse(serializedState) || {};
    } catch (err) {
        return {};
    }
};

export const loadState = () => {
    try {
        const { persist = {}, ...newState } = getStorageState();
        // Get the stored customer ID
        const customerId = fromState.Auth.customer.getId(newState);
        if (customerId) {
            const customerPersist = persist[customerId];
            if (customerPersist) {
                return merge({}, newState, { persist: customerPersist }, { urlParams });
            }
        }
        return { ...newState, urlParams };
    } catch (err) {
        return { urlParams };
    }
};

let savedState = '';
export const saveState = state => {
    try {
        const serializedState = JSON.stringify(state);
        localStorage.setItem(localStorageKey, serializedState);
        savedState = serializedState;
    } catch {
        // ignore write errors
    }
};

const initialState = loadState();
const reducer = reduceReducers((state, action) => {
    // This reducer is used to recover customer persitent data after a customer switch
    switch (action.type) {
        case NavActionTypes.changeCustomer: {
            return merge({}, state, loadState());
        }
    }
    return state;
}, rootReducer);

/**
 * Initializes a new Redux store using provided initialState
 *
 * @param {Object} initialState
 * @return {import("redux").Store} A new Redux store
 */
export const configureStore = initialState => {
    // Amplify Middleware is the first in the chain, so it can dispatch actions related to authentication after all state is updated
    const middlewares = [AmplifyMiddleware, ...middleware, reduxThunk];
    const composeEnhancers =
        (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ && // add support for Redux dev tools
            window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
                actionsDenylist: ignoredActions,
                actionSanitizer,
            })) ||
        compose;
    let store;
    try {
        store = createStore(
            reducer,
            initialState,
            composeEnhancers(applyMiddleware(...middlewares))
        );
    } catch (error) {
        // Bad local storage store
        store = createStore(reducer, {}, composeEnhancers(applyMiddleware(...middlewares)));
    }

    return store;
};

const store = configureStore(initialState);

setStoreRef(store);

store.dispatch(
    DBActions.init({
        [customerSchema.key]: [BROWSE_CUSTOMER].map(formatCustomer),
    })
);

export const onStateUpdate = providedState => {
    const state = providedState || store.getState();
    const rememberMe = fromState.Auth.shouldRemember(state);
    const customerId = fromState.Auth.customer.getId(state);
    const { persist = {} } = getStorageState();
    if (customerId && !rememberMe) {
        // Clear customer specific data when rememberMe is disabled
        delete persist[customerId];
    } else if (customerId) {
        // Persist customer specific data inside this object. It will be merged with the main state upon customer switch
        persist[customerId] = {
            assessmentsReporting: {
                filters: fromState.AssessmentsReporting.getFilters(state),
            },
            dashboard: {
                preferences: fromState.Dashboard.getPreferences(state),
            },
            dsar: {
                requestList: getPersistedRequestListState(state),
                actionItemList: getPersistedActionItemListState(state),
                formList: getPersistedFormListState(state),
                formListView: getPersistedFormListViewState(state),
                workflowList: getPersistedWorkflowListState(state),
            },
            dsarReporting: {
                filters: fromState.DSARReporting.getFilters(state),
            },
            uc: {
                dashboard: getPersistedDashboardState(state),
                privacyProtocolList: getPersistedPrivacyProtocolListState(state),
                configList: getPersistedUCConfigListState(state),
                consumerSsoList: getPersistedConsumerSsoListState(state),
                integrationsList: getPersistedIntegrationsListState(state),
            },
            vendors: {
                vendorList: getPersistedVendorListState(state),
            },
            configList: {
                filters: getConfigListFilters(state),
                sort: getConfigListSort(state),
                sortDirection: getConfigListSortDirection(state),
            },
            cmpClassifications: Object.values(STORE_TYPES).reduce(
                (acc, storeType) => ({
                    ...acc,
                    [storeType]: Object.values(CLASSIFICATION_VIEWS).reduce(
                        (acc, view) => ({
                            ...acc,
                            [view]: getPersistedClassification(state, storeType, view),
                        }),
                        {}
                    ),
                }),
                {}
            ),
        };
    }
    const newState = {
        auth: {
            customer: {
                id: customerId,
            },
            rememberMe,
            user: {
                id: fromState.Auth.user.getId(state),
            },
        },
        persist,
        ui: {
            preventWarning: fromState.UI.getPreventWarning(state),
            resultsView: fromState.UI.getResultsView(state),
        },
    };
    if (JSON.stringify(newState) !== savedState) {
        saveState(newState);
    }
};

store.subscribe(onStateUpdate);

export default store;
